<template>
    <base-section v-if="section" :section="section" :show-config-ui="showConfigUi" @edit-section="$emit('edit-section')"
                  @delete-section="$emit('delete-section')">
        <template v-if="hasDateStat" #toolbar>
            <span>时间统计间隔：</span>
            <el-select class="yq-stat-section-interval-select" v-model="dateStatInterval" size="mini"
                       @change="loadData">
                <el-option v-for="item in dateStatIntervals" :key="item.value" :label="item.label"
                           :value="item.value"></el-option>
            </el-select>
        </template>
        <div class="yq-stat-section-chart">
            <v-chart v-if="chart" :options="chart.options" :init-options="chart.initOptions" autoresize
                     @click="onClickChart" theme="dark"></v-chart>
        </div>
    </base-section>
</template>

<script>
    import BaseSection from './BaseSection'
    import sectionService from '../../service/section'
    import statSectionService from '../../service/stat-section'
    import _ from 'underscore'
    import ECharts from '../../plugins/echarts'
    import HtmlUtils from '../../service/html-utils'
    import qs from 'qs'
    import moment from 'moment'

    const colors = ['#2A9CFF', '#03C773', '#EB5F03', '#7E2FA9', '#F19604', '#4158EA', '#00D5BF', '#B8E986',
        '#BFD52A']
    function tooltipFormatter(params) {
        if (!params.data) {
            return
        }
        const html = []
        const data = params.data
        const path = [...data.path, data.name]
        const dimensionConfigs = data.dimensionConfigs
        path.forEach((value, index) => {
            html.push(HtmlUtils.escapeHtml(dimensionConfigs[index].name), '：', HtmlUtils.escapeHtml(value), '<br/>')
        })
        html.push('数据量：', data.value)
        const result = html.join('')
        return result
    }

    function getDataName(data){
        if(data.interval){
            if(data.interval.endsWith('m')){
                return moment(data.key).format('YYYY-MM-DD HH:mm')
            }else if(data.interval.endsWith('h')){
                return moment(data.key).format('YYYY-MM-DD HH')
            }else if(data.interval.endsWith('d')){
                return moment(data.key).format('YYYY-MM-DD')
            }else if(data.interval.endsWith('w')){
                return moment(data.key).format('YYYY-MM-DD')
            }else if(data.interval.endsWith('M')){
                return moment(data.key).format('YYYY-MM')
            }else if(data.interval.endsWith('q')){
                return moment(data.key).format('YYYY-MM')
            }else if(data.interval.endsWith('y')){
                return moment(data.key).format('YYYY')
            }else {
                return moment(data.key).format('YYYY-MM-DD HH:mm:ss')
            }
        }else{
            return data.key
        }
    }

    const dateStatIntervals = [
        {value: 'auto', label: '自动'},
        {value: '1m', label: '分钟'},
        {value: '1h', label: '小时'},
        {value: '1d', label: '天'},
        {value: '1w', label: '周'},
        {value: '1M', label: '月'},
        {value: '1q', label: '季度'},
        {value: '1y', label: '年'}
    ]

    function computeStatDepth(config) {
        if (config.type === 'terms') {
            if (config.termsStatConfig.subDimensionConfig) {
                return 1 + computeStatDepth(config.termsStatConfig.subDimensionConfig)
            } else {
                return 1
            }
        } else if (config.type === 'date') {
            if (config.dateStatConfig.subDimensionConfig) {
                return 1 + computeStatDepth(config.dateStatConfig.subDimensionConfig)
            } else {
                return 1
            }
        } else {
            let maxSubDepth = 0
            for (const valueConfig of config.customStatConfig.valueConfigs) {
                if (valueConfig.subDimensionConfig) {
                    const subDepth = computeStatDepth(valueConfig.subDimensionConfig)
                    if (subDepth > maxSubDepth) {
                        maxSubDepth = subDepth
                    }
                }
            }
            return 1 + maxSubDepth
        }
    }

    export default {
        name: "StatSection",
        components: {BaseSection, 'v-chart': ECharts},
        props: {
            id: Number,
            showConfigUi: Boolean,
            refreshId: {
                type: Number,
                default: 0
            },
            pageId: Number,
            templateId: Number,
            timeInterval: String
        },
        data() {
            return {
                dateStatIntervals,
                dateStatInterval: 'auto',
                section: null,
                sectionConfig: null,
                data: null,
                chart: null
            }
        },
        watch: {
            id(){
                this.init()
            },
            pageId(){
                this.init()
            },
            templateId(){
              this.init()
            },
            refreshId() {
                this.init()
            },
            timeInterval() {
                this.init()
            }
        },
        created() {
            this.init()
        },
        computed: {
            hasDateStat(){
                if(this.sectionConfig){
                    const queue = [this.sectionConfig.dimensionConfig]
                    while(queue.length>0){
                        const config = queue.shift()
                        if (config.type === 'terms') {
                            if (config.termsStatConfig.subDimensionConfig) {
                                queue.push(config.termsStatConfig.subDimensionConfig)
                            }
                        } else if (config.type === 'date') {
                            return true
                        } else {
                            for (const valueConfig of config.customStatConfig.valueConfigs) {
                                if (valueConfig.subDimensionConfig) {
                                    queue.push(valueConfig.subDimensionConfig)
                                }
                            }
                        }
                    }
                }
                return false
            }
        },
        methods: {
            async init() {
                const response = await sectionService.getSection(this.id, {templateId: this.templateId})
                this.section = response.data
                if (this.section.config) {
                    this.sectionConfig = JSON.parse(this.section.config)
                    await this.loadData()
                }
            },
            async loadData(){
                const dataResponse = await statSectionService.getStatData({
                    pageId: this.pageId,
                    templateId: this.templateId,
                    sectionId: this.id,
                    timeInterval: this.timeInterval,
                    dateStatInterval: this.dateStatInterval
                })
                this.data = dataResponse.data
                const queue = [...this.data]
                while(queue.length>0){
                    const item = queue.shift()
                    item.name = getDataName(item)
                    if(item.subDimensionData){
                        queue.push(...item.subDimensionData)
                    }
                }
                if (this.sectionConfig.chartType === 'tag_cloud') {
                    this.drawTagCloudChart()
                } else if (this.sectionConfig.chartType === 'pie') {
                    this.drawPieChart()
                } else if (this.sectionConfig.chartType === 'bar') {
                    this.drawBarChart()
                } else if (this.sectionConfig.chartType === 'line') {
                    this.drawLineChart()
                } else if (this.sectionConfig.chartType === 'map') {
                    this.drawMapChart()
                }
            },
            drawTagCloudChart() {
                this.chart = {
                    options: {
                        tooltip: {
                            show: true,
                            trigger: 'item',
                            formatter: tooltipFormatter
                        },
                        toolbox: {
                            show: true,
                            feature: {
                                dataView: {readOnly: false},
                                saveAsImage: {}
                            },
                            iconStyle: {
                                color: '#000000',
                                borderColor: '#FC8F22'
                            }
                        },
                        series: [{
                            name: this.sectionConfig.dimensionConfig.name,
                            type: 'wordCloud',
                            data: this.data.map((item,index) => {
                                return {
                                    name: item.name,
                                    key: item.key,
                                    value: item.value,
                                    interval: item.interval,
                                    path: [],
                                    dimensionConfigs: [this.sectionConfig.dimensionConfig],
                                    textStyle: {
                                        normal: {
                                            color: colors[index%colors.length]
                                        }
                                    }
                                }
                            })
                        }]
                    },
                    initOptions: {}
                }
            },
            drawPieChart() {
                const statDepth = computeStatDepth(this.sectionConfig.dimensionConfig)
                if (statDepth === 1) {
                    // 简单饼图
                    this.chart = {
                        options: {
                            color: colors,
                            tooltip: {
                                show: true,
                                trigger: 'item',
                                formatter: tooltipFormatter
                            },
                            toolbox: {
                                show: true,
                                feature: {
                                    dataView: {readOnly: false},
                                    saveAsImage: {}
                                },
                                iconStyle: {
                                    color: '#000000',
                                    borderColor: '#FC8F22'
                                }
                            },
                            series: [{
                                name: this.sectionConfig.dimensionConfig.name,
                                type: 'pie',
                                label: {show:false},
                                radius: ['50%', '75%'],
                                data: this.data.map(item => {
                                    return {
                                        name: item.name,
                                        key: item.key,
                                        value: item.value,
                                        interval: item.interval,
                                        path: [],
                                        dimensionConfigs: [this.sectionConfig.dimensionConfig]
                                    }
                                })
                            }]
                        },
                        initOptions: {}
                    }
                } else {
                    // 多圆饼图
                    const seriesArray = []
                    const queue = [...this.data, {path: [], dimensionConfigs: [this.sectionConfig.dimensionConfig]}]
                    while (queue.length > 0) {
                        const item = queue.shift()
                        if (item.hasOwnProperty('path')) {
                            continue
                        }
                        let foundMark = null
                        for (const mark of queue) {
                            if (mark.hasOwnProperty('path')) {
                                foundMark = mark
                                break
                            }
                        }
                        const dimensionConfig = foundMark.dimensionConfigs[foundMark.dimensionConfigs.length - 1]
                        let series = seriesArray[foundMark.path.length]
                        if (!series) {
                            const radiusUnit = 80 / (statDepth * 3 - 1)
                            const currentDepth = foundMark.path.length
                            const innerRadius = radiusUnit * (currentDepth * 3)
                            const outerRadius = radiusUnit * ((currentDepth + 1) * 3 - 1)
                            series = {
                                name: dimensionConfig.name,
                                type: 'pie',
                                radius: [innerRadius + '%', outerRadius + '%'],
                                data: []
                            }
                            seriesArray[foundMark.path.length] = series
                        }
                        const path = [...foundMark.path, item.key]
                        series.data.push({
                            name: item.name,
                            key: item.key,
                            value: item.value,
                            interval: item.interval,
                            path: foundMark.path,
                            dimensionConfigs: foundMark.dimensionConfigs
                        })
                        if (item.subDimensionData) {
                            let subDimensionConfig
                            if (dimensionConfig.type === 'terms') {
                                subDimensionConfig = dimensionConfig.termsStatConfig.subDimensionConfig
                            } else if (dimensionConfig.type === 'custom') {
                                for (const valueConfig of dimensionConfig.valueConfigs) {
                                    if (valueConfig.name === item.key) {
                                        subDimensionConfig = valueConfig.subDimensionConfig
                                        break
                                    }
                                }
                            }
                            const dimensionConfigs = [...foundMark.dimensionConfigs, subDimensionConfig]
                            queue.splice(0, 0, ...item.subDimensionData, {
                                path,
                                dimensionConfigs
                            })
                        }
                    }
                    this.chart = {
                        options: {
                            color: colors,
                            tooltip: {
                                show: true,
                                trigger: 'item',
                                formatter: tooltipFormatter
                            },
                            toolbox: {
                                show: true,
                                feature: {
                                    dataView: {readOnly: false},
                                    saveAsImage: {}
                                },
                                iconStyle: {
                                    color: '#000000',
                                    borderColor: '#FC8F22'
                                }
                            },
                            series: seriesArray
                        },
                        initOptions: {}
                    }
                }
            },
            drawBarChart() {
                const statDepth = computeStatDepth(this.sectionConfig.dimensionConfig)
                if (statDepth === 1) {
                    // 一维柱状图
                    this.chart = {
                        options: {
                            color: colors,
                            tooltip: {
                                show: true,
                                trigger: 'item',
                                formatter: tooltipFormatter
                            },
                            toolbox: {
                                show: true,
                                feature: {
                                    dataView: {readOnly: false},
                                    saveAsImage: {}
                                },
                                iconStyle: {
                                    color: '#000000',
                                    borderColor: '#FC8F22'
                                }
                            },
                            xAxis: {
                                type: 'category',
                                axisLabel: {show:false},
                                data: this.data.map(item => item.name)
                            },
                            yAxis: {
                                type: 'value',
                                splitLine: false
                            },
                            series: [{
                                name: '数据量',
                                barMaxWidth: 60,
                                data: this.data.map(item => {
                                    return {
                                        name: item.name,
                                        key: item.key,
                                        value: item.value,
                                        interval: item.interval,
                                        path: [],
                                        dimensionConfigs: [this.sectionConfig.dimensionConfig]
                                    }
                                }),
                                type: 'bar'
                            }]
                        },
                        initOptions: {}
                    }
                } else {
                    // 二维柱状图
                    const xAxisData = _.uniq(this.data.flatMap(item => item.subDimensionData || []).map(item => item.name))
                    const seriesArray = this.data.map(item => {
                        return {
                            name: item.key,
                            type: 'bar',
                            data: xAxisData.map(name => {
                                const subDimensionData = item.subDimensionData || []
                                const d2 = subDimensionData.find(d2 => d2.name === name)
                                if (!d2) {
                                    return null
                                }
                                let subDimensionConfig
                                if (this.sectionConfig.dimensionConfig.type === 'terms') {
                                    subDimensionConfig = this.sectionConfig.dimensionConfig.termsStatConfig.subDimensionConfig
                                } else if (this.sectionConfig.dimensionConfig.type === 'custom') {
                                    for (const valueConfig of this.sectionConfig.dimensionConfig.customStatConfig.valueConfigs) {
                                        if (valueConfig.name === d2.key) {
                                            subDimensionConfig = valueConfig.subDimensionConfig
                                            break
                                        }
                                    }
                                }
                                return {
                                    name: d2.name,
                                    key: d2.key,
                                    value: d2.value,
                                    interval: d2.interval,
                                    path: [item.key],
                                    dimensionConfigs: [this.sectionConfig.dimensionConfig, subDimensionConfig]
                                }
                            })
                        }
                    })
                    this.chart = {
                        options: {
                            color: colors,
                            tooltip: {
                                show: true,
                                trigger: 'axis'
                            },
                            toolbox: {
                                show: true,
                                feature: {
                                    dataView: {readOnly: false},
                                    saveAsImage: {}
                                },
                                iconStyle: {
                                    color: '#000000',
                                    borderColor: '#FC8F22'
                                }
                            },
                            xAxis: {
                                type: 'category',
                                axisLabel: {show:false},
                                data: xAxisData
                            },
                            yAxis: {
                                type: 'value',
                                splitLine: false
                            },
                            series: seriesArray
                        },
                        initOptions: {}
                    }
                }
            },
            drawLineChart() {
                const statDepth = computeStatDepth(this.sectionConfig.dimensionConfig)
                if (statDepth === 1) {
                    // 一维折线图
                    this.chart = {
                        options: {
                            color: colors,
                            tooltip: {
                                show: true,
                                trigger: 'item',
                                formatter: tooltipFormatter
                            },
                            toolbox: {
                                show: true,
                                feature: {
                                    dataView: {readOnly: false},
                                    saveAsImage: {}
                                },
                                iconStyle: {
                                    color: '#000000',
                                    borderColor: '#FC8F22'
                                }
                            },
                            xAxis: {
                                type: 'category',
                                axisLabel: {show:false},
                                data: this.data.map(item => item.name)
                            },
                            yAxis: {
                                type: 'value',
                                splitLine: false
                            },
                            series: [{
                                name: '数据量',
                                data: this.data.map(item => {
                                    return {
                                        name: item.name,
                                        key: item.key,
                                        value: item.value,
                                        interval: item.interval,
                                        path: [],
                                        dimensionConfigs: [this.sectionConfig.dimensionConfig]
                                    }
                                }),
                                type: 'line',
                                itemStyle: {
                                    color: '#FFFFFF'
                                },
                                lineStyle: {
                                    color: '#D35EEA'
                                },
                                areaStyle: {
                                    color: {
                                        type: 'linear',
                                        x: 0,
                                        y: 0,
                                        x2: 0,
                                        y2: 1,
                                        colorStops: [{
                                            offset: 0, color: 'rgba(211, 94, 234, 1)' // 0% 处的颜色
                                        }, {
                                            offset: 1, color: 'rgba(211, 94, 234, 0)' // 100% 处的颜色
                                        }],
                                        global: false // 缺省为 false
                                    }
                                }
                            }]
                        },
                        initOptions: {}
                    }
                } else {
                    // 二维折线图
                    const xAxisData = _.uniq(this.data.flatMap(item => item.subDimensionData || []).map(item => item.name))
                    const seriesArray = this.data.map(item => {
                        return {
                            name: item.key,
                            type: 'line',
                            data: xAxisData.map(name => {
                                const subDimensionData = item.subDimensionData || []
                                const d2 = subDimensionData.find(d2 => d2.name === name)
                                if (!d2) {
                                    return null
                                }
                                let subDimensionConfig
                                if (this.sectionConfig.dimensionConfig.type === 'terms') {
                                    subDimensionConfig = this.sectionConfig.dimensionConfig.termsStatConfig.subDimensionConfig
                                } else if (this.sectionConfig.dimensionConfig.type === 'custom') {
                                    for (const valueConfig of this.sectionConfig.dimensionConfig.customStatConfig.valueConfigs) {
                                        if (valueConfig.name === d2.key) {
                                            subDimensionConfig = valueConfig.subDimensionConfig
                                            break
                                        }
                                    }
                                }
                                return {
                                    name: d2.name,
                                    key: d2.key,
                                    value: d2.value,
                                    interval: d2.interval,
                                    path: [item.key],
                                    dimensionConfigs: [this.sectionConfig.dimensionConfig, subDimensionConfig]
                                }
                            })
                        }
                    })
                    this.chart = {
                        options: {
                            color: colors,
                            tooltip: {
                                show: true,
                                trigger: 'axis'
                            },
                            toolbox: {
                                show: true,
                                feature: {
                                    dataView: {readOnly: false},
                                    saveAsImage: {}
                                },
                                iconStyle: {
                                    color: '#000000',
                                    borderColor: '#FC8F22'
                                }
                            },
                            xAxis: {
                                type: 'category',
                                axisLabel: {show:false},
                                data: xAxisData
                            },
                            yAxis: {
                                type: 'value',
                                splitLine: false
                            },
                            series: seriesArray
                        },
                        initOptions: {}
                    }
                }
            },
            drawMapChart() {
                const valueStat = this.data.reduce((previousValue, currentValue) => {
                    if (previousValue.min === null || currentValue.value < previousValue.min) {
                        previousValue.min = currentValue.value
                    }
                    if (previousValue.max === null || currentValue.value > previousValue.max) {
                        previousValue.max = currentValue.value
                    }
                    return previousValue
                }, {min: null, max: null})
                this.chart = {
                    options: {
                        tooltip: {
                            show: true,
                            trigger: 'item',
                            formatter: tooltipFormatter
                        },
                        toolbox: {
                            show: true,
                            feature: {
                                dataView: {readOnly: false},
                                saveAsImage: {}
                            },
                            iconStyle: {
                                color: '#000000',
                                borderColor: '#FC8F22'
                            }
                        },
                        visualMap: {
                            min: valueStat.min,
                            max: valueStat.max,
                            text: ['High', 'Low'],
                            realtime: false,
                            calculable: true,
                            inRange: {
                                color: ['lightskyblue', 'yellow', 'orangered']
                            }
                        },
                        series: [{
                            name: this.sectionConfig.dimensionConfig.name,
                            data: this.data.map(item => {
                                return {
                                    name: item.name,
                                    key: item.key,
                                    value: item.value,
                                    interval: item.interval,
                                    path: [],
                                    dimensionConfigs: [this.sectionConfig.dimensionConfig]
                                }
                            }),
                            type: 'map',
                            mapType: this.sectionConfig.mapChartConfig.position
                        }]
                    },
                    initOptions: {}
                }
            },
            onClickChart(params) {
                if (params.componentType === 'series') {
                    if (!params.data) {
                        return
                    }
                    const data = params.data
                    const url = document.location.origin + document.location.pathname + '#/main/stat-section/news-list'
                    const query = {
                        sectionId: this.id,
                        pageId: this.pageId,
                        templateId: this.templateId,
                        timeInterval: this.timeInterval,
                        dateStatInterval: data.interval,
                        path: [...data.path, data.key]
                    }
                    const rootDimensionConfig = data.dimensionConfigs[0]
                    switch (rootDimensionConfig.type) {
                        case 'terms':
                            query.datasourceId = rootDimensionConfig.termsStatConfig.datasource.id
                            query.timeField = rootDimensionConfig.termsStatConfig.timeField
                            break
                        case 'date':
                            query.datasourceId = rootDimensionConfig.dateStatConfig.datasource.id
                            query.timeField = rootDimensionConfig.dateStatConfig.timeField
                            break
                        case 'custom':
                            for (const valueConfig of rootDimensionConfig.customStatConfig.valueConfigs) {
                                if (valueConfig.name === query.path[0]) {
                                    query.datasourceId = valueConfig.datasource.id
                                    query.timeField = valueConfig.timeField
                                    break
                                }
                            }
                            break
                    }
                    window.open(url + '?' + qs.stringify(query, {arrayFormat: 'repeat'}), '_blank')
                }
            }
        }
    }
</script>

<style scoped>

</style>
